import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import axios from 'axios';
import { useI18n } from 'next-localization';

import { RichLabelProps } from 'components/Shared/JabraLabel';

import { CountrySelectorViewModel } from '../../types/countrySelector';
import JabraSelect from '../Shared/JabraSelect';

const CountrySelector: React.FC<ValueFieldProps<CountrySelectorViewModel>> = (props) => {
  const { t } = useI18n();
  const {
    field,
    isValid,
    value: initialValue,
    onChange,
    fieldValidationErrorsComponent,
    labelComponent,
  } = props;
  const { id, name } = field.valueField;
  const { countries, countriesWithStates, required, shouldStatesDroprownRender } = field.model;
  const [value, setValue] = useState(initialValue);
  const [selectedCountry, setSelectedCountry] = useState(initialValue);
  const hasAttemptedAutoDetection = useRef(false);

  const [states, setStates] = useState(
    countriesWithStates.find((country) => value === country.value)?.states
  );
  const FieldValidationErrors = fieldValidationErrorsComponent as FC;
  const Label = labelComponent as FC<RichLabelProps>;

  const setCountryValue = useCallback(
    (newValue: string) => {
      setValue(newValue);
      setSelectedCountry(newValue);
      onChange(name, newValue, isValid, []);

      const selectedCountryStates = countriesWithStates.find(
        (country) => newValue === country.value
      )?.states;
      setStates(selectedCountryStates);
    },
    [countriesWithStates, isValid, name, onChange]
  );

  const onStateChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newValue = event?.target?.value;
      setValue(newValue);
      onChange(name, newValue, isValid, []);
    },
    [isValid, name, onChange]
  );

  const onCountryChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newValue = event?.target?.value;
      setCountryValue(newValue);
    },
    [setCountryValue]
  );

  useEffect(() => {
    async function fetchIpLocation() {
      if (!hasAttemptedAutoDetection.current && !initialValue) {
        hasAttemptedAutoDetection.current = true;
        try {
          const {
            data: { countryCode },
          } = await axios.get<{ countryCode: string }>('/api/getIpLocation');

          const matchedCountry = countries.find((country) => country.isoCode === countryCode);

          if (matchedCountry) {
            setCountryValue(matchedCountry.value);
          }
        } catch (e) {
          // Silently fail - location detection is optional and should not affect form functionality
        }
      }
    }
    if (typeof window !== 'undefined') {
      void fetchIpLocation();
    }
  }, [countries, setCountryValue, initialValue]);

  return (
    <>
      {!!Label && <Label {...props} />}
      <JabraSelect
        id={id}
        name={name}
        value={selectedCountry}
        jssMetadata={props}
        required={required}
        onChange={onCountryChange}
      >
        {countries.map(({ name, countryName, isoCode, value }) => (
          <option key={`${name}-${isoCode}`} value={value}>
            {name || countryName}
          </option>
        ))}
      </JabraSelect>
      {shouldStatesDroprownRender && !!countriesWithStates?.length && !!states && (
        <JabraSelect
          id={`${id}__States`}
          name={`${name}__States`}
          jssMetadata={props}
          className="mt-6"
          required
          onChange={onStateChange}
        >
          <option value="">{t('system.WebformSelectState')}</option>
          {states?.map(({ name, isoCode }) => (
            <option value={`${selectedCountry}-${isoCode}`} key={`${name}-${isoCode}`}>
              {name}
            </option>
          ))}
        </JabraSelect>
      )}
      {!!FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};
export default CountrySelector;
